import { computed, ref, watch } from 'vue';
import { deliveryteamOrdersApiClient } from '@api-client/deliveryteam_orders';
import { callNative, isInReactNativeWebView } from '@lib/deliveryteamCourierWebviewBridge';
// import { IS_PROD } from '@lib/utils/getEnvVariable';
import {
    LOCATION_UPDATE_THROTTLE_MS,
    MINIMUM_MOVEMENT_METERS
} from '@lib/deliveryteamCourierWebviewBridge/location/expo-location-consts';
import { registerWebHandler } from '@lib/deliveryteamCourierWebviewBridge';
// import { toast } from '@/ui-plus/sonner';
import { activeDeliveryteams } from './MyDeliveryteams';
import { backgroundLocationPermission, foregroundLocationPermission } from './locationPermission';

/** Inside RN webview Expo owns location + API send; web only updates UI refs. */
const inRnWebview = isInReactNativeWebView();

export const currentUserLat = ref<number | null>(null);
export const currentUserLng = ref<number | null>(null);
export const currentUserHeading = ref<number | null>(null);

export const overrideCurrentUserLat = ref<number | null>(null);
export const overrideCurrentUserLng = ref<number | null>(null);
export const locationWatchError = ref<string | null>(null);

/** True after `watchPosition` runs (after a click, or reload when permission was already granted). */
export const isUserLocationWatchActive = ref(false);

/** Courier flows need live coordinates before the dashboard is usable. */
export const hasAccessibleUserLocation = computed(
    () => currentUserLat.value != null && currentUserLng.value != null,
);

registerWebHandler('setUserLocation', ({ coords: { latitude, longitude, heading } }) => {
    const newLat = overrideCurrentUserLat.value || latitude;
    const newLng = overrideCurrentUserLng.value || longitude;
    currentUserHeading.value = heading != null && Number.isFinite(heading) ? heading : null;
    pointChanged(newLat, newLng);
    // In RN webview Expo is the location source; never start the browser watch.
    // In plain-browser bridging cases we still want the browser watch to stop.
    if (!inRnWebview) {
        stopGeolocationWatch();
    }
});



let watchId: number | null = null;
let lastSentAt = 0;
let lastSentLat: number | null = null;
let lastSentLng: number | null = null;

export function getCurrentUserLocationForOrderActions() {
    if (currentUserLat.value == null || currentUserLng.value == null) {
        return null;
    }
    return {
        locationLat: currentUserLat.value,
        locationLng: currentUserLng.value,
    };
}

function toRadians(value: number) {
    return value * (Math.PI / 180);
}

function distanceInMeters(lat1: number, lng1: number, lat2: number, lng2: number) {
    const earthRadiusMeters = 6_371_000;
    const dLat = toRadians(lat2 - lat1);
    const dLng = toRadians(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
        + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2))
        * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusMeters * c;
}

function isLocationAbleToUpdate(lat: number, lng: number) {
    const now = Date.now();
    if (lastSentLat === null || lastSentLng === null) {
        return true;
    }

    const timePassed = now - lastSentAt >= LOCATION_UPDATE_THROTTLE_MS;
    const previousLat = lastSentLat;
    const previousLng = lastSentLng;
    const movedMeters = distanceInMeters(previousLat, previousLng, lat, lng);
    const movedEnough = movedMeters >= MINIMUM_MOVEMENT_METERS;

    return timePassed || movedEnough;
}

setTimeout(() => {
    if (inRnWebview) return; // Expo handles the API send; nothing to schedule here.
    console.log('watch activeDeliveryteams');
    watch(activeDeliveryteams, () => {
        if (currentUserLat.value != null && currentUserLng.value != null) {
            sendLocation(currentUserLat.value, currentUserLng.value);
        }
    }, { immediate: false });
}, 3000);

async function sendLocation(lat: number, lng: number) {
    if (activeDeliveryteams.value.length > 0) {
        try {
            const response = await (await deliveryteamOrdersApiClient.courier_update_location.$post({
                json: { currentLat: lat, currentLng: lng },
            })).json();

            if (response.error) {
                console.error('Failed to update courier location', response.error);
            }
        } catch (error) {
            console.error('Failed to update courier location', error);
        }

        lastSentAt = Date.now();
        lastSentLat = lat;
        lastSentLng = lng;
        // toast.message('Your location is being updated');
    } else {
        // ignore
    }
}


export function pointChanged(lat: number, lng: number) {
    currentUserLat.value = lat;
    currentUserLng.value = lng;
    locationWatchError.value = null;

    // Inside RN webview the Expo bg task is the source of truth for API sends.
    if (inRnWebview) return;

    if (isLocationAbleToUpdate(lat, lng)) {
        void sendLocation(lat, lng);
    }
}

function startGeolocationWatch() {
    if (watchId !== null) return;
    if (!('geolocation' in navigator)) {
        locationWatchError.value = 'Geolocation is not supported in this browser.';
        return;
    }

    // toast.success(`startGeolocationWatch`);
    watchId = navigator.geolocation.watchPosition(
        (position) => {
            // position.coords.accuracy;
            // position.coords.altitude;
            // position.coords.altitudeAccuracy;
            // position.coords.heading;
            // position.coords.latitude;
            // position.coords.longitude;
            // position.coords.speed;
            const lat = overrideCurrentUserLat.value || position.coords.latitude;
            const lng = overrideCurrentUserLng.value || position.coords.longitude;
            const h = position.coords.heading;
            currentUserHeading.value = h != null && Number.isFinite(h) ? h : null;
            pointChanged(lat, lng);
        },
        (error) => {
            locationWatchError.value = error.message;
            console.error('Unable to watch courier geolocation', error);
        },
        {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: 20_000,
        }
    );
    isUserLocationWatchActive.value = true;
}

function stopGeolocationWatch() {
    if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
        watchId = null;
    }
}

/**
 * If the user already allowed geolocation for this origin, start watching without a click.
 * When status is `prompt`, we do not start here (Chrome blocks non-gesture prompts).
 * Safari / some browsers may not support this query — then the Orders home button is used.
 */
export async function tryStartGeolocationWatchIfAlreadyGranted() {
    if (inRnWebview) return; // Expo owns the watch; nothing to do in the SPA.
    if (watchId !== null) return;
    if (!('geolocation' in navigator)) return;

    const permissions = navigator.permissions;
    if (!permissions?.query) return;

    try {
        const status = await permissions.query({ name: 'geolocation' as PermissionName });
        const syncFromStatus = () => {
            if (status.state === 'granted') {
                startGeolocationWatch();
            }
        };
        syncFromStatus();
        status.addEventListener('change', syncFromStatus);
    } catch {
        // Geolocation not exposed to Permissions API in this browser — fall back to tap-to-start.
    }
}

/**
 * Restart the watcher and clear the last error so the user can retry after changing
 * permission or recovering from timeout.
 */
export function retryUserLocationAccess() {
    locationWatchError.value = null;
    if (inRnWebview) return; // Expo handles retries via the bg task.
    stopGeolocationWatch();
    startGeolocationWatch();
}

setTimeout(() => {
    retryUserLocationAccess()
}, 3_000);

setInterval(() => {
    if (inRnWebview) return; // Expo runs its own throttled send loop.
    if (currentUserLat.value !== null && currentUserLng.value !== null) {
        if (isLocationAbleToUpdate(currentUserLat.value, currentUserLng.value)) {
            void sendLocation(currentUserLat.value, currentUserLng.value);
        }
    }
}, 5_000)

let foregroundLocationInterval: ReturnType<typeof setInterval> | null = null;
const foregroundLocationHandler = async () => {
    if (foregroundLocationPermission.value?.granted || backgroundLocationPermission.value?.granted) {
        // const position = await callNative('getLastKnownPositionAsync', {
        //     maxAge: 3 * 60 * 1000, // 3 minutes
        //     requiredAccuracy: 4,
        // });
        const position = await callNative('getCurrentPositionAsync', {
            accuracy: 4,
        });
        if (position) {
            currentUserLat.value = position.coords.latitude;
            currentUserLng.value = position.coords.longitude;
            currentUserHeading.value = position.coords.heading;
            if (isLocationAbleToUpdate(currentUserLat.value, currentUserLng.value)) {
                void sendLocation(currentUserLat.value, currentUserLng.value);
            }
        }
    }
}

const stopForegroundLocationHandler = () => {
    if (foregroundLocationInterval) {
        clearInterval(foregroundLocationInterval);
        foregroundLocationInterval = null;
    }
}

// ---- RN webview driver: start / stop the native bg location task based on
// whether the courier has any active deliveryteam. Web is the source of "active"
// knowledge; Expo just executes start/stop.
if (inRnWebview) {
    let nativeTrackingDesired = false;

    const syncNativeTracking = () => {
        const shouldTrack = activeDeliveryteams.value.length > 0;
        if (shouldTrack === nativeTrackingDesired) return;
        nativeTrackingDesired = shouldTrack;
        if (shouldTrack) {
            stopForegroundLocationHandler();
            foregroundLocationInterval = setInterval(
                foregroundLocationHandler,
                LOCATION_UPDATE_THROTTLE_MS
            );
            void callNative('startLocationTracking', {}).catch((e) => {
                nativeTrackingDesired = false;
                locationWatchError.value = e instanceof Error ? e.message : String(e);
                console.error('Failed to start native location tracking', e);
            });
        } else {
            stopForegroundLocationHandler();
            void callNative('stopLocationTracking', undefined).catch((e) => {
                console.error('Failed to stop native location tracking', e);
            });
        }
    };

    watch(activeDeliveryteams, syncNativeTracking, { immediate: false });

    // Reconcile with whatever native is already doing after webview reloads.
    setTimeout(() => {
        void callNative('isLocationTrackingActive', undefined)
            .then((active) => {
                nativeTrackingDesired = !!active;
                syncNativeTracking();
            })
            .catch(() => {
                // No bridge yet (still loading) — first watch tick will trigger anyway.
                syncNativeTracking();
            });
    }, 3_000);
}